import { Popover, PopoverOrigin, PopoverProps } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useSearch } from '@client/hooks/use-search.hook';
import { MENU_ITEM_HEIGHT } from '@shared/constants';
import { SelectListChildren, NodonMenuProps } from '../menu.model';
import SelectList, { SelectListProps } from '@ui/SelectList';
import { useObjectMemo } from '@client/hooks/use-object.hooks';

export type SelectMenuProps<T> = SelectListProps<T> & NodonMenuProps;

const SelectMenu = <T,>({
  items,
  anchor,
  anchorPosition,
  enableSearch = false,
  enableVirtualization = false,
  width = 'auto',
  emptyStateProps,
  listProps,
  onClose,
  onSearch,
  children,
  ...popoverProps
}: SelectMenuProps<T>) => {
  const [searchString, setSearchString] = useState('');
  const filteredItems = useSearch(items, searchString);

  const popoverSlotProps = useMemo<PopoverProps['slotProps']>(() => {
    const padding = 6;
    const itemsLength = enableSearch
      ? filteredItems.length + 1
      : filteredItems.length;

    const h = itemsLength * MENU_ITEM_HEIGHT + padding * 2;

    return {
      paper: {
        sx: {
          display: 'flex',
          flexDirection: 'column',
          paddingTop: enableVirtualization ? `${padding}px` : 0,
          height: enableVirtualization ? h : 'auto',
          width: enableVirtualization ? width : 'auto',
        },
      },
    };
  }, [enableSearch, filteredItems.length, width, enableVirtualization]);

  const anchorOrigin = useObjectMemo<PopoverOrigin>({
    vertical: 'bottom',
    horizontal: 'left',
  });

  const handleSearch = useCallback(
    (searchString: string) => {
      onSearch?.(searchString);
      setSearchString(searchString);
    },
    [onSearch, setSearchString],
  );

  const renderChildren = useCallback<SelectListChildren<T>>(
    (item, style, index) => {
      if (typeof children === 'function') {
        return children(item, style, index);
      }
    },
    [children],
  );
  return (
    <Popover
      anchorEl={anchor}
      anchorPosition={anchorPosition}
      anchorReference={anchor ? 'anchorEl' : 'anchorPosition'}
      open={!!anchor || !!anchorPosition}
      marginThreshold={0}
      anchorOrigin={anchorOrigin}
      slotProps={popoverSlotProps}
      onClose={onClose}
      {...popoverProps}
    >
      <SelectList
        items={filteredItems}
        searchString={searchString}
        width={width}
        enableSearch={enableSearch}
        enableVirtualization={enableVirtualization}
        emptyStateProps={emptyStateProps}
        listProps={listProps}
        onSearch={handleSearch}
      >
        {typeof children === 'function' ? renderChildren : children}
      </SelectList>
    </Popover>
  );
};

export default SelectMenu;
